import { createSelector } from '@ngrx/store';
import { AccountTypes, FilteredAccount, IRATypes, RejectedAccount } from '@shared/models';
import { getConfigClientFromRTE } from '@src/app/core/config/config-client';
import { selectMultiITFromSelectedAccount } from '@store/internal-transfers/selectors/multi-internal-transfer.selectors';
import { getIsGiftingFlow } from '@store/reducers/triage/triage.reducer';
import { selectSelectedPositions } from '@store/selectors/cost-basis/cost-basis.selectors';

import * as fromClientAccounts from '../../reducers/client-accounts/client-accounts.reducer';
import * as fromFeature from '../../reducers/index';
import {
  selectGiftingAdvisedAccountsToggle,
  selectInternalGiftingManagedAccountsToggle,
  selectNonVCOrgTransferToggle,
  selectVCOrgToOrgToggle,
} from '../../selectors/feature/feature.selectors';
import {
  selectInternalTransferFrom,
  selectInternalTransferFromAccountType,
  selectInternalTransferToAccountType,
} from '../internal-transfer/internal-transfer.selectors';
import {
  selectedAccountsFrom,
  selectIraType,
} from '../selected-client-accounts/selected-client-accounts.selectors';
import { selectIsIraAccount, selectTriageState } from '../triage/triage.selectors';

export const selectClientAccountsState = createSelector(
  fromFeature.getClientAccountsState,
  (state) => state,
);

export const selectClientAccountsEntity = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getClientAccountsEntity,
);
export const selectClientAccountsLoaded = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getClientAccountsLoaded,
);
export const selectClientAccountsLoading = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getClientAccountsLoading,
);

export const selectDisplayedAccounts = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getDisplayedAccounts,
);

export const selectEligibleJointAccounts = (isTransferTo: boolean = false) =>
  createSelector(
    selectClientAccountsState,
    selectTriageState,
    selectInternalGiftingManagedAccountsToggle,
    selectGiftingAdvisedAccountsToggle,
    (
      clientAccountState,
      triageState,
      isInternalGiftingFromManagedEnabled,
      isAdvisedAccountToggleOn,
    ) => {
      const showManagedAccounts =
        (isInternalGiftingFromManagedEnabled && getConfigClientFromRTE().isInternal) ||
        isAdvisedAccountToggleOn;
      return getIsGiftingFlow(triageState)
        ? fromClientAccounts.getEligibleJointAccounts(
            clientAccountState,
            isTransferTo,
            showManagedAccounts,
          )
        : fromClientAccounts.getEligibleWROSJointAccounts(clientAccountState, isTransferTo);
    },
  );

export const selectEligibleMinorAccounts = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getEligibleMinorAccounts,
);

export const selectEligibleInternalTransferFromAccountsWithRejects = (
  rejectedAccounts: RejectedAccount[] = [],
) =>
  createSelector(
    selectClientAccountsState,
    selectInternalTransferFromAccountType,
    selectInternalTransferToAccountType,
    selectVCOrgToOrgToggle,
    selectNonVCOrgTransferToggle,
    (
      state,
      transferFromAccountType,
      transferToAccountType,
      vcOrgToOrgToggle,
      nonVcOrgTransferToggle,
    ) =>
      fromClientAccounts.getEligibleInternalTransferFromAccounts(
        state,
        fromClientAccounts.isSpecificAccountType(transferFromAccountType)
          ? transferFromAccountType
          : null,
        rejectedAccounts,
        fromClientAccounts.isSpecificAccountType(transferToAccountType)
          ? transferToAccountType
          : null,
        vcOrgToOrgToggle,
        nonVcOrgTransferToggle,
      ),
  );

//IT Transfer-from Selector
export const selectEligibleInternalTransferFromAccounts =
  selectEligibleInternalTransferFromAccountsWithRejects();

export const selectEligibleInternalTransferToAccountsWithRejects = (
  rejectedAccounts: RejectedAccount[] = [],
) =>
  createSelector(
    selectClientAccountsState,
    selectInternalTransferToAccountType,
    selectInternalTransferFrom,
    selectSelectedPositions,
    selectInternalTransferFromAccountType,
    selectVCOrgToOrgToggle,
    selectNonVCOrgTransferToggle,
    (
      state,
      transferToAccountType,
      transferFromAccount,
      selectedPositions,
      transferFromAccountType,
      vcOrgToOrgToggle,
      nonVcOrgTransferToggle,
    ) =>
      fromClientAccounts.getEligibleInternalTransferToAccounts(
        state,
        fromClientAccounts.isSpecificAccountType(transferToAccountType)
          ? transferToAccountType
          : null,
        transferFromAccount,
        selectedPositions,
        rejectedAccounts,
        fromClientAccounts.isSpecificAccountType(transferFromAccountType)
          ? transferFromAccountType
          : null,
        vcOrgToOrgToggle,
        nonVcOrgTransferToggle,
      ),
  );

//IT Transfer-to Selector
export const selectEligibleInternalTransferToAccounts =
  selectEligibleInternalTransferToAccountsWithRejects();

export const selectEligibleIndividualAccounts = (isTransferTo: boolean = true) =>
  createSelector(selectClientAccountsState, (clientAccountState) =>
    fromClientAccounts.getEligibleIndividualAccounts(clientAccountState, isTransferTo),
  );

export const selectEligibleTraditionalIRAAccounts = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getEligibleTraditionalIRAAccounts,
);

export const selectEligibleRothIRAAccounts = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getEligibleRothIRAAccounts,
);

export const selectEligibleClientAccount = (isTransferTo: boolean = false) =>
  createSelector(
    selectIsIraAccount,
    selectIraType,
    selectClientAccountsState,
    (isIraFlow, iraType, state) =>
      fromClientAccounts.getEligibleClientAccount(isIraFlow, iraType, state, isTransferTo),
  );

export const selectSpecificEligibleClientAccount = (
  isIRAAccount: boolean = false,
  iraType: IRATypes = IRATypes.UNKNOWN,
  isTransferTo: boolean = true,
) =>
  createSelector(selectClientAccountsState, (state) =>
    fromClientAccounts.getEligibleClientAccount(isIRAAccount, iraType, state, isTransferTo),
  );

export const selectEligibleTrustInheritanceAccounts = createSelector(
  selectClientAccountsState,
  fromClientAccounts.getEligibleTrustInheritanceAccounts,
);

export const selectEligibleGiftingAccounts = (accountType: AccountTypes) =>
  createSelector(
    selectClientAccountsState,
    selectInternalGiftingManagedAccountsToggle,
    selectGiftingAdvisedAccountsToggle,
    (state, isInternalGiftingFromManagedEnabled, isAdvisedAccountToggleOn) => {
      const showManagedAccounts =
        (isInternalGiftingFromManagedEnabled && getConfigClientFromRTE().isInternal) ||
        isAdvisedAccountToggleOn;
      switch (accountType) {
        case AccountTypes.INDV:
          return fromClientAccounts.getEligibleIndividualGiftingAccounts(
            state,
            showManagedAccounts,
          );
        case AccountTypes.TRUST:
          return fromClientAccounts.getEligibleTrustGiftingAccounts(state, showManagedAccounts);
        default:
          return { accounts: [] as FilteredAccount[] };
      }
    },
  );

export const selectEligibleGiftingAccountsMO = (accountType: AccountTypes) =>
  createSelector(
    selectClientAccountsState,
    selectInternalGiftingManagedAccountsToggle,
    selectGiftingAdvisedAccountsToggle,
    (state, isInternalGiftingFromManagedEnabled, isAdvisedAccountToggleOn) => {
      const showManagedAccounts =
        (isInternalGiftingFromManagedEnabled && getConfigClientFromRTE().isInternal) ||
        isAdvisedAccountToggleOn;
      switch (accountType) {
        case AccountTypes.INDV:
          return fromClientAccounts.getEligibleGiftingAccountsForMO(
            state,
            fromClientAccounts.Eligible_Individual_Account_Types,
            true,
            showManagedAccounts,
          );
        case AccountTypes.TRUST:
          return fromClientAccounts.getEligibleGiftingAccountsForMO(
            state,
            fromClientAccounts.Eligible_Trust_Account_Types,
            false,
            showManagedAccounts,
          );
        default:
          return { accounts: [] as FilteredAccount[] };
      }
    },
  );

export const selectSelectedAccountFromIsManaged = createSelector(
  selectedAccountsFrom,
  (selectedFromAccounts) => {
    return selectedFromAccounts?.length ? selectedFromAccounts[0].accountData?.managed : false;
  },
);

export const selectVgClientPoid = createSelector(
  selectClientAccountsState,
  (state) => state.entity?.vgClientPoid,
);

// True when the client owns at least one Vanguard Charitable account. Used by the
// Summary-Continue routing to decide whether the VC-only toggle path can continue
// (client has VC accounts to transfer) or must hand off to Intelledox.
export const selectClientHasVCAccounts = createSelector(selectClientAccountsEntity, (entity) =>
  (entity?.accounts ?? []).some((account) => account.isVanguardCharitable === true),
);

/**
 * ORG-transfer routing decision.
 *
 * At Continue-from-Summary for an IT / Multi-IT transfer with FROM=ORG:
 *   - NON_VC toggle ON                                → continue in the flow
 *   - NON_VC OFF, VC ON, client has VC accounts       → continue in the flow
 *   - NON_VC OFF, VC ON, client has NO VC accounts    → route to Intelledox
 *   - NON_VC OFF, VC OFF                              → route to Intelledox
 *
 * Downstream eligibility (funded ORG accounts) is handled by the existing
 * eligibility gate; this helper only decides between "hand off to Intelledox"
 * and "continue in the flow".
 */
export const shouldOrgTransferContinue = (
  nonVcOrgTransferToggle: boolean,
  vcOrgToOrgToggle: boolean,
  clientHasVCAccounts: boolean,
): boolean => nonVcOrgTransferToggle || (vcOrgToOrgToggle && clientHasVCAccounts);

export const E_CONSENTER_FALLBACK = 'Econsenter';

const trimOrEmpty = (s?: string) => s?.trim() ?? '';

// Resolves from single-IT first, then multi-IT — the terms page is shared and
// the FROM lives in whichever slice the user actually walked through.
export const selectEConsenter = createSelector(
  selectInternalTransferFrom,
  selectMultiITFromSelectedAccount,
  (transferFrom, multiFrom) => {
    const account = transferFrom?.selectedAccount?.accountData ?? multiFrom?.accountData ?? null;
    const firstName = trimOrEmpty(account?.eConsenterFirstName);
    const lastName = trimOrEmpty(account?.eConsenterLastName);
    const hasAnyName = !!(firstName || lastName);
    // Fallback applies only when the BE provided no name at all. When a first
    // name exists without a last name we keep lastName empty so the crew script
    // doesn't render the placeholder mid-sentence.
    return {
      name: hasAnyName ? [firstName, lastName].filter(Boolean).join(' ') : E_CONSENTER_FALLBACK,
      lastName: hasAnyName ? lastName : E_CONSENTER_FALLBACK,
    };
  },
);
